import { useParams, useSearchParams } from 'react-router-dom';
import {
  Decimal,
  formaterDate,
  formaterIdentite,
  formaterMontant,
  formaterMontantDevise,
  formaterPourcentage,
  formaterQuantite,
  sensVariation,
} from '@gsm/partage';
import { useRessource, messageErreur } from '../api/ressource';
import { BandeauAlerte } from '../composants/BandeauAlerte';

interface Poche {
  reel: string;
  cible: string | null;
}

interface Position {
  libelle: string;
  type: string;
  quantite: string;
  pru: string;
  /** `null` faute de cours : la ligne est valorisée à son PRU. */
  cours: string | null;
  valeur: string;
  poids: string;
  plusValueLatente: string | null;
  coursAbsent: boolean;
}

interface Releve {
  entete: {
    client: { reference: string; nom: string; prenom: string };
    mandat: { reference: string };
    profil: { code: string; libelle: string };
    agence: { code: string; libelle: string };
    periode: { annee: number; trimestre: number; dateDebut: string; dateFin: string };
    dateValorisation: string | null;
    dateSituation: string | null;
  };
  synthese: {
    actifNetOuverture: string;
    versements: string;
    retraits: string;
    frais: string;
    revenus: string;
    actifNetCloture: string;
  };
  repartition: { actions: Poche; taux: Poche; opcvm: Poche; tresorerie: Poche } | null;
  positions: Position[];
  mouvements: { date: string; type: string; libelle: string; montant: string; signe: string }[];
}

const LIBELLE_MOUVEMENT: Record<string, string> = {
  VERSEMENT: 'Versement',
  RETRAIT: 'Retrait',
  ACHAT_TITRE: 'Achat de titres',
  VENTE_TITRE: 'Vente de titres',
  DIVIDENDE: 'Dividende',
  COUPON: 'Coupon',
  REMBOURSEMENT: 'Remboursement',
  DISTRIBUTION_OPCVM: 'Distribution OPCVM',
  FRAIS_GESTION: 'Frais de gestion',
  FRAIS_COURTAGE: 'Frais de courtage',
  REGULARISATION: 'Régularisation',
};

/** Couleur des plus-values : le seul endroit du relevé où le vert/rouge porte du sens. */
function classePerf(valeur: string | null): string | undefined {
  if (valeur === null) return undefined;
  const sens = sensVariation(new Decimal(valeur));
  return sens === 'nul' ? undefined : sens;
}

export function Releve() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const annee = params.get('annee') ?? String(new Date().getUTCFullYear());
  const trimestre = params.get('trimestre') ?? '1';
  const { donnees, chargement, erreur } = useRessource<Releve>(
    `/reporting/mandats/${id}/trimestriel?annee=${annee}&trimestre=${trimestre}`,
  );

  if (chargement) return <p className="surtitre releve__attente">Préparation du relevé…</p>;
  if (erreur || !donnees)
    return (
      <div className="releve__attente">
        <BandeauAlerte nature="refus" titre="Relevé indisponible">
          {messageErreur(erreur) ?? 'Impossible de produire ce relevé.'}
        </BandeauAlerte>
      </div>
    );

  const r = donnees;
  const e = r.entete;
  const s = r.synthese;
  const sansCours = r.positions.some((p) => p.coursAbsent);
  const situationAnterieure =
    e.dateSituation !== null &&
    e.dateValorisation !== null &&
    e.dateSituation.slice(0, 10) !== e.dateValorisation.slice(0, 10);

  return (
    <div className="releve">
      <div className="releve__barre">
        <button type="button" className="bouton" onClick={() => window.print()}>
          Imprimer / Enregistrer en PDF
        </button>
      </div>

      <article className="releve__feuille">
        <header className="releve__entete">
          <div className="releve__marque">
            GSM<span>.</span>
            <em>Relevé de gestion trimestriel</em>
          </div>
          <div className="releve__periode">
            <strong>
              T{e.periode.trimestre} {e.periode.annee}
            </strong>
            <span>
              {formaterDate(e.periode.dateDebut)} — {formaterDate(e.periode.dateFin)}
            </span>
            <span>Arrêté au {formaterDate(e.dateValorisation)}</span>
            {situationAnterieure && <span>Positions au {formaterDate(e.dateSituation)}</span>}
          </div>
        </header>

        <section className="releve__identite">
          <div>
            <span className="surtitre">Client</span>
            <div>{formaterIdentite(e.client.nom, e.client.prenom)}</div>
            <div className="releve__ref">{e.client.reference}</div>
          </div>
          <div>
            <span className="surtitre">Mandat</span>
            <div>{e.mandat.reference}</div>
            <div className="releve__ref">
              {e.agence.code} — {e.agence.libelle}
            </div>
          </div>
          <div>
            <span className="surtitre">Profil de gestion</span>
            <div>{e.profil.libelle}</div>
            <div className="releve__ref">{e.profil.code}</div>
          </div>
        </section>

        <h2 className="releve__titre">Synthèse</h2>
        <table className="tableau releve__synthese">
          <tbody>
            <tr>
              <td>Actif net d’ouverture</td>
              <td className="num">{formaterMontantDevise(s.actifNetOuverture)}</td>
            </tr>
            <tr>
              <td>Versements</td>
              <td className="num">{formaterMontantDevise(s.versements)}</td>
            </tr>
            <tr>
              <td>Retraits</td>
              <td className="num">{formaterMontantDevise(s.retraits)}</td>
            </tr>
            <tr>
              <td>Frais de gestion</td>
              <td className="num">{formaterMontantDevise(s.frais)}</td>
            </tr>
            <tr className="releve__total">
              <td>Actif net de clôture</td>
              <td className="num">{formaterMontantDevise(s.actifNetCloture)}</td>
            </tr>
          </tbody>
        </table>

        {r.repartition && (
          <>
            <h2 className="releve__titre">Répartition par poche</h2>
            <table className="tableau">
              <thead>
                <tr>
                  <th>Poche</th>
                  <th className="num">Poids réel</th>
                  <th className="num">Cible</th>
                </tr>
              </thead>
              <tbody>
                {(
                  [
                    ['Actions', r.repartition.actions],
                    ['Taux', r.repartition.taux],
                    ['OPCVM', r.repartition.opcvm],
                    ['Trésorerie', r.repartition.tresorerie],
                  ] as [string, Poche][]
                ).map(([libelle, poche]) => (
                  <tr key={libelle}>
                    <td>{libelle}</td>
                    <td className="num">{formaterPourcentage(poche.reel)}</td>
                    <td className="num">
                      {poche.cible === null ? '—' : formaterPourcentage(poche.cible)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <h2 className="releve__titre">Détail des positions</h2>
        <table className="tableau">
          <thead>
            <tr>
              <th>Instrument</th>
              <th className="num">Quantité</th>
              <th className="num">PRU</th>
              <th className="num">Cours</th>
              <th className="num">Valeur</th>
              <th className="num">Poids</th>
              <th className="num">+/− value</th>
            </tr>
          </thead>
          <tbody>
            {r.positions.length === 0 ? (
              <tr>
                <td colSpan={7} className="tableau__vide">
                  Aucune position.
                </td>
              </tr>
            ) : (
              r.positions.map((p) => (
                <tr key={p.libelle}>
                  <td>{p.libelle}</td>
                  <td className="num">{formaterQuantite(p.quantite)}</td>
                  <td className="num">{formaterMontant(p.pru)}</td>
                  <td className="num">
                    {p.coursAbsent ? `${formaterMontant(null)}\u00a0*` : formaterMontant(p.cours)}
                  </td>
                  <td className="num">{formaterMontantDevise(p.valeur)}</td>
                  <td className="num">{formaterPourcentage(p.poids)}</td>
                  <td className={`num ${classePerf(p.plusValueLatente) ?? ''}`}>
                    {formaterMontantDevise(p.plusValueLatente)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {sansCours && (
          <p className="releve__note">
            * Aucun cours disponible à la date d’arrêté : la ligne est valorisée à son prix de
            revient, sans plus-value latente.
          </p>
        )}
        {situationAnterieure && (
          <p className="releve__note">
            Positions transmises par le teneur de compte au {formaterDate(e.dateSituation)},
            valorisées aux cours du {formaterDate(e.dateValorisation)}.
          </p>
        )}

        <h2 className="releve__titre">Mouvements de la période</h2>
        <table className="tableau">
          <thead>
            <tr>
              <th className="num">Date</th>
              <th>Nature</th>
              <th>Libellé</th>
              <th className="num">Montant</th>
            </tr>
          </thead>
          <tbody>
            {r.mouvements.length === 0 ? (
              <tr>
                <td colSpan={4} className="tableau__vide">
                  Aucun mouvement sur la période.
                </td>
              </tr>
            ) : (
              r.mouvements.map((m, i) => (
                <tr key={i}>
                  <td className="num">{formaterDate(m.date)}</td>
                  <td>{LIBELLE_MOUVEMENT[m.type] ?? m.type}</td>
                  <td>{m.libelle}</td>
                  <td className="num">
                    {m.signe} {formaterMontantDevise(m.montant)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <footer className="releve__pied">
          <p>
            Mandat de gestion discrétionnaire. Les décisions d’investissement sont prises par la
            société de gestion dans le cadre du profil convenu. Les performances passées ne
            préjugent pas des performances futures. Document produit par la société de gestion et
            diffusé par l’agence de rattachement.
          </p>
        </footer>
      </article>
    </div>
  );
}
